/**
 * File view component.
 * @module components/theme/View/FileView
 */

import React, { useState, useEffect } from 'react';

import leftIcon from './arrow-left.svg';
import rightIcon from './arrow-right.svg';

const PdfView = ({ content }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const [windowWidth, setWindowWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 0;
  });

  // Load react-pdf only in the browser.
  const [pdfComponents, setPdfComponents] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    let mounted = true;

    const loadPdf = async () => {
      try {
        const { Document, Page, pdfjs } = await import('react-pdf');

        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

        if (mounted) {
          setPdfComponents({ Document, Page });
          setErrorMessage('');
        }
      } catch (error) {
        setErrorMessage('Failed to load PDF viewer', error);
      }
    };

    loadPdf();

    return () => {
      mounted = false;
    };
  }, []);

  // Track window width.
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goToPreviousPage = () => {
    setPageNumber((current) => Math.max(1, current - 1));
  };

  const goToNextPage = () => {
    setPageNumber((current) => Math.min(numPages, current + 1));
  };

  // Avoid rendering PDF.js during SSR.
  if (!pdfComponents) {
    return <div className="pdf-loading">PDF wird geladen...</div>;
  }

  const { Document, Page } = pdfComponents;

  return (
    <>
      <Document
        file={content.file.download}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        <Page
          pageNumber={pageNumber}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          scale={2}
          canvasBackground="white"
          width={windowWidth < 1300 ? windowWidth * 0.465 : 580}
        />
      </Document>

      <div className="pdf-controls">
        <button
          onClick={goToPreviousPage}
          className={`pdf-button ${pageNumber === 1 ? 'disabled' : ''}`}
          disabled={pageNumber === 1}
          aria-label="Vorherige Seite"
        >
          <span
            className="icon"
            style={{ backgroundImage: `url(${leftIcon})` }}
          />
        </button>

        <span className="pageNum">
          Seite {pageNumber} von {numPages}
        </span>

        <button
          onClick={goToNextPage}
          className={`pdf-button ${pageNumber === numPages ? 'disabled' : ''}`}
          disabled={pageNumber === numPages || numPages === 0}
          aria-label="Nächste Seite"
        >
          <span
            className="icon"
            style={{ backgroundImage: `url(${rightIcon})` }}
          />
        </button>
      </div>
      {errorMessage}
    </>
  );
};

export default PdfView;
